import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Clock, Loader2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SlideTiming = ({ pageId }) => {
  const [duration, setDuration] = useState(5);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages/${pageId}`);
        setDuration(response.data.slide_duration || 5);
      } catch (error) {
        console.error('Failed to load slide timing:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [pageId]);

  const handleSave = async (newDuration) => {
    const val = Math.max(3, Math.min(120, newDuration));
    setDuration(val);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { slide_duration: val });
      toast.success(`Slide duration set to ${val} seconds`);
    } catch (error) {
      toast.error('Failed to update slide timing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const presets = [5, 10, 15, 30, 60];

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="slide-timing-config">
      <CardContent className="p-6">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-400" /> Slide Timing
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Set how long each image/weather slide is displayed. Videos play to completion.
        </p>
        
        {/* Duration control */}
        <div className="flex items-center gap-3 mb-4">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 w-9 p-0"
            onClick={() => handleSave(duration - 1)}
            disabled={duration <= 3 || saving}
          >
            <Minus className="w-4 h-4" />
          </Button>
          
          <div className="bg-slate-950 border border-slate-700 rounded-md px-4 py-2 min-w-[80px] text-center">
            <span className="text-white text-lg font-medium" data-testid="slide-duration-value">{duration}</span>
            <span className="text-slate-400 text-sm ml-1">sec</span>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 w-9 p-0"
            onClick={() => handleSave(duration + 1)}
            disabled={duration >= 120 || saving}
          >
            <Plus className="w-4 h-4" />
          </Button>
          
          {saving && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
        </div>

        {/* Quick presets */}
        <div className="flex gap-2 flex-wrap">
          {presets.map((preset) => (
            <Button
              key={preset}
              size="sm"
              variant={duration === preset ? 'default' : 'outline'}
              className={duration === preset 
                ? 'bg-cyan-500 hover:bg-cyan-600 h-8' 
                : 'border-slate-700 text-slate-300 hover:bg-slate-800 h-8'}
              onClick={() => handleSave(preset)}
              disabled={saving}
              data-testid={`preset-${preset}s`}
            >
              {preset}s
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SlideTiming;
